import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { categoryHubData } from "@/mocks/categoryData";
import { useState } from "react";

const catColorMap: Record<string, { bg: string; text: string; border: string }> = {
  primary: { bg: "bg-primary-500", text: "text-primary-500", border: "border-primary-200" },
  accent: { bg: "bg-accent-500", text: "text-accent-500", border: "border-accent-200" },
  secondary: { bg: "bg-secondary-500", text: "text-secondary-500", border: "border-secondary-200" },
};

export default function ContentHub() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24">
          <div className="w-full px-4 md:px-6">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-foreground-900 leading-tight">
                {categoryHubData.title}
              </h1>
              <p className="mt-3 text-lg md:text-2xl font-heading text-accent-500">
                {categoryHubData.subtitle}
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                {categoryHubData.description}
              </p>
            </div>

            {/* Category cards */}
            <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {categoryHubData.categories.map((cat) => {
                const c = catColorMap[cat.color];
                return (
                  <Link
                    key={cat.slug}
                    to={`/content/${cat.slug}`}
                    className="group relative rounded-2xl overflow-hidden h-[320px] md:h-[380px] cursor-pointer"
                  >
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      title={cat.name}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-block px-2.5 py-1 rounded-full ${c.bg} text-background-50 text-xs font-semibold`}>
                          {cat.tag}
                        </span>
                        <span className="text-xs text-background-300">{cat.count}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-full ${c.bg}/20`}>
                          <i className={`${cat.icon} ${c.text} text-lg`}></i>
                        </div>
                        <h3 className="text-xl md:text-2xl font-heading font-bold text-background-50">
                          {cat.name}
                        </h3>
                      </div>
                      <p className="text-sm text-background-200/80">{cat.desc}</p>
                      <div className="mt-3 flex items-center gap-1 text-xs text-background-400 group-hover:text-background-200 transition-colors">
                        <span>进入频道</span>
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Latest articles grid */}
        <section className="w-full bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-10 max-w-6xl mx-auto">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                最新爆料<span className="text-primary-500">实时更新</span>
              </h2>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></span>
                <span className="text-xs text-foreground-500">实时追踪中</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {categoryHubData.latestArticles.map((article) => {
                const catSlugMap: Record<string, string> = {
                  "网红翻车": "wanghong",
                  "反差视频": "fancha",
                  "酒店猛料": "jiudian",
                };
                const slug = catSlugMap[article.category] || "wanghong";
                const detailMap: Record<string, string> = {
                  "latest-1": "wh-1", "latest-2": "jd-1", "latest-3": "fc-1",
                  "latest-4": "wh-2", "latest-5": "jd-2", "latest-6": "fc-2",
                };
                const detailId = detailMap[article.id] || "wh-1";
                return (
                <Link
                  key={article.id}
                  to={`/content/${slug}/detail/${detailId}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden cursor-pointer border border-background-200/70 hover:border-accent-300 transition-colors"
                >
                  <div className="relative w-full h-[200px] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      title={article.title}
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs text-accent-500 font-medium">{article.category}</span>
                      <span className="text-xs text-foreground-500">{article.time}</span>
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                  </div>
                </Link>
              );
              })}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <NewsletterCTA />
      </main>

      <Footer />
    </div>
  );
}

function NewsletterCTA() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [statusMsg, setStatusMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>(".hn-field");
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      setStatusMsg("订阅成功！");
      setEmail("");
      return;
    }
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const fd = new URLSearchParams();
      fd.append("email", email);
      fd.append("source", "hub_newsletter");
      await fetch("https://readdy.ai/api/form/d90hoh84jh3v1cgksmbg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: fd.toString(),
      });
      setStatus("success");
      setStatusMsg("订阅成功！大瓜第一时间送到你手上。");
      setEmail("");
    } catch {
      setStatus("error");
      setStatusMsg("网络异常，请稍后重试");
    }
  };

  return (
    <section className="relative w-full py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20abstract%20gradient%20with%20amber%20and%20deep%20charcoal%2C%20soft%20glowing%20effect%2C%20cinematic%20mood%2C%20luxurious%20dark%20texture%2C%20no%20people&width=1920&height=600&seq=hub-cta-bg-01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/60"></div>
      </div>
      <div className="relative z-10 w-full px-4 md:px-6 text-center">
        <h2 className="font-heading text-3xl md:text-5xl font-bold text-background-50">
          不想错过任何大瓜？
        </h2>
        <p className="mt-3 text-sm md:text-base text-background-300">
          订阅深夜基地，爆料第一时间直达你手中
        </p>
        <form onSubmit={handleSubmit} data-readdy-form className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
          <input type="text" name="website_alt" className="hn-field" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <input
            type="email" name="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setStatus("idle"); }}
            placeholder="输入邮箱订阅爆料"
            className="flex-1 w-full sm:w-auto px-5 py-3 rounded-full bg-background-50/10 border border-background-300/30 text-background-50 text-sm placeholder-foreground-500 outline-none focus:border-accent-400 transition-colors"
            required
          />
          <button type="submit" disabled={status === "loading"} className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50">
            {status === "loading" ? <i className="ri-loader-4-line animate-spin"></i> : "立即订阅"}
          </button>
        </form>
        {status !== "idle" && (
          <p className={`mt-3 text-sm ${status === "success" ? "text-accent-400" : "text-primary-400"}`}>{statusMsg}</p>
        )}
      </div>
    </section>
  );
}